/**
 * Offload Responsibilities — Freedom Engine Assessment & Plan Builder.
 *
 * Full integration of @roi-pros/freedom-engine (pure deterministic planner).
 * Flow: Assessment → Score → Initiative Selection → Plan → Pricing.
 */

import { useCallback, useMemo, useState } from 'react';
import {
  buildPlan,
  calculateFreedomScore,
  calculatePricing,
  formatClientOutput,
  loadFrameworkDataset,
  type AssessmentAnswers,
  type ClientOutput,
  type FrameworkDataset,
  type FreedomScoreResult,
  type Plan,
  type PricingResult,
} from '@roi-pros/freedom-engine';
import frameworkJson from '@roi-pros/freedom-engine/data/freedom-framework.json';

const dataset: FrameworkDataset = loadFrameworkDataset(frameworkJson);

type Step = 'assess' | 'score' | 'plan';

export function OffloadResponsibilitiesPage() {
  const [step, setStep] = useState<Step>('assess');
  const [answers, setAnswers] = useState<AssessmentAnswers>({});
  const [scoreResult, setScoreResult] = useState<FreedomScoreResult | null>(null);
  const [selectedInitiativeIds, setSelectedInitiativeIds] = useState<string[]>([]);
  const [plan, setPlan] = useState<Plan | null>(null);
  const [pricing, setPricing] = useState<PricingResult | null>(null);
  const [clientOutput, setClientOutput] = useState<ClientOutput | null>(null);

  const allQuestions = useMemo(
    () =>
      dataset.pillars.flatMap((pillar) =>
        pillar.categories.flatMap((cat) =>
          cat.questions.map((q) => ({
            ...q,
            pillarName: pillar.name,
            categoryName: cat.name,
          })),
        ),
      ),
    [],
  );

  const isAssessmentComplete = allQuestions.every((q) => answers[q.id] != null);

  const handleAnswer = useCallback((questionId: string, value: number) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  }, []);

  const handleScore = useCallback(() => {
    const result = calculateFreedomScore(answers, dataset);
    setScoreResult(result);
    setSelectedInitiativeIds(dataset.initiatives.map((i) => i.id));
    setStep('score');
  }, [answers]);

  const handleBuildPlan = useCallback(() => {
    const builtPlan = buildPlan({ selectedInitiativeIds }, dataset);
    setPlan(builtPlan);
    const pricingResult = calculatePricing(builtPlan, builtPlan.capacityTierId, dataset);
    setPricing(pricingResult);
    setClientOutput(formatClientOutput(builtPlan, pricingResult));
    setStep('plan');
  }, [selectedInitiativeIds]);

  const handleStartOver = useCallback(() => {
    setStep('assess');
    setAnswers({});
    setScoreResult(null);
    setSelectedInitiativeIds([]);
    setPlan(null);
    setPricing(null);
    setClientOutput(null);
  }, []);

  const toggleInitiative = useCallback((id: string) => {
    setSelectedInitiativeIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  }, []);

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-2">Offload Responsibilities</h1>
      <p className="text-sm text-gray-500 mb-8">
        Assess your operations, identify automation opportunities, and build a plan to free up your time.
      </p>

      <div className="flex items-center gap-2 mb-8">
        {(['assess', 'score', 'plan'] as const).map((s, i) => {
          const order = (['assess', 'score', 'plan'] as const).indexOf(step);
          const isActive = step === s;
          const isDone = order > i;
          return (
            <div key={s} className="flex items-center gap-2">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  isActive
                    ? 'bg-legacy-600 text-white'
                    : isDone
                      ? 'bg-legacy-100 text-legacy-600'
                      : 'bg-gray-100 text-gray-400'
                }`}
              >
                {i + 1}
              </div>
              <span className={`text-sm ${isActive ? 'font-semibold text-gray-900' : 'text-gray-400'}`}>
                {s === 'assess' ? 'Assessment' : s === 'score' ? 'Results' : 'Plan'}
              </span>
              {i < 2 && <div className="w-8 h-px bg-gray-200" />}
            </div>
          );
        })}
      </div>

      {step === 'assess' && (
        <AssessmentStep
          questions={allQuestions}
          answers={answers}
          onAnswer={handleAnswer}
          onSubmit={handleScore}
          isComplete={isAssessmentComplete}
        />
      )}

      {step === 'score' && scoreResult && (
        <ScoreStep
          result={scoreResult}
          initiatives={dataset.initiatives}
          selectedIds={selectedInitiativeIds}
          onToggleInitiative={toggleInitiative}
          onBuildPlan={handleBuildPlan}
          onBack={() => setStep('assess')}
        />
      )}

      {step === 'plan' && plan && pricing && clientOutput && (
        <PlanStep
          plan={plan}
          pricing={pricing}
          clientOutput={clientOutput}
          onStartOver={handleStartOver}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Steps
// -----------------------------------------------------------------------------

interface QuestionWithContext {
  id: string;
  text: string;
  scale: number;
  pillarName: string;
  categoryName: string;
}

function AssessmentStep({
  questions,
  answers,
  onAnswer,
  onSubmit,
  isComplete,
}: {
  questions: QuestionWithContext[];
  answers: AssessmentAnswers;
  onAnswer: (id: string, value: number) => void;
  onSubmit: () => void;
  isComplete: boolean;
}) {
  const grouped = useMemo(() => {
    const map = new Map<string, QuestionWithContext[]>();
    for (const q of questions) {
      const group = map.get(q.pillarName) ?? [];
      group.push(q);
      map.set(q.pillarName, group);
    }
    return Array.from(map.entries());
  }, [questions]);

  return (
    <div className="space-y-8">
      {grouped.map(([pillarName, qs]) => (
        <div key={pillarName} className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{pillarName}</h2>
          <div className="space-y-5">
            {qs.map((q) => (
              <div key={q.id}>
                <p className="text-sm text-gray-700 mb-2">
                  <span className="text-xs text-gray-400 mr-1">{q.categoryName}</span>
                  {q.text}
                </p>
                <div className="flex gap-2">
                  {Array.from({ length: q.scale }, (_, i) => i + 1).map((val) => (
                    <button
                      key={val}
                      onClick={() => onAnswer(q.id, val)}
                      className={`w-10 h-10 rounded-lg text-sm font-medium border transition-colors ${
                        answers[q.id] === val
                          ? 'bg-legacy-600 text-white border-legacy-600'
                          : 'bg-white text-gray-600 border-gray-200 hover:border-legacy-300'
                      }`}
                    >
                      {val}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <button
        onClick={onSubmit}
        disabled={!isComplete}
        className="w-full py-3 bg-legacy-600 text-white font-medium rounded-xl hover:bg-legacy-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        Calculate Freedom Score
      </button>
    </div>
  );
}

function ScoreStep({
  result,
  initiatives,
  selectedIds,
  onToggleInitiative,
  onBuildPlan,
  onBack,
}: {
  result: FreedomScoreResult;
  initiatives: readonly {
    id: string;
    name: string;
    pillar: string;
    category: string;
    effortHours: number;
    complexity: string;
    description: string;
  }[];
  selectedIds: string[];
  onToggleInitiative: (id: string) => void;
  onBuildPlan: () => void;
  onBack: () => void;
}) {
  return (
    <div className="space-y-6">
      <div className="card text-center">
        <p className="text-sm text-gray-500 mb-1">Your Freedom Score</p>
        <p className="text-5xl font-bold text-legacy-600">{Math.round(result.overallScore)}</p>
        <p className="mt-2 text-lg font-medium text-gray-900">{result.band.name}</p>
        <p className="text-sm text-gray-500 mt-1">{result.band.description}</p>
      </div>

      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Pillar Breakdown</h2>
        <div className="space-y-3">
          {result.pillarScores.map((ps) => (
            <div key={ps.pillarId}>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-700">{ps.pillarName}</span>
                <span className="font-medium text-gray-900">{Math.round(ps.score)}%</span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2">
                <div
                  className="bg-legacy-500 h-2 rounded-full transition-all"
                  style={{ width: `${ps.score}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Recommended Initiatives</h2>
        <p className="text-sm text-gray-500 mb-4">Select which initiatives to include in your plan.</p>
        <div className="space-y-2">
          {initiatives.map((init) => (
            <label
              key={init.id}
              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                selectedIds.includes(init.id)
                  ? 'border-legacy-300 bg-legacy-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <input
                type="checkbox"
                checked={selectedIds.includes(init.id)}
                onChange={() => onToggleInitiative(init.id)}
                className="mt-0.5 rounded border-gray-300 text-legacy-600 focus:ring-legacy-500"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">{init.name}</p>
                <p className="text-xs text-gray-500 mt-0.5">{init.description}</p>
                <div className="flex gap-3 mt-1">
                  <span className="text-xs text-gray-400">{init.pillar}</span>
                  <span className="text-xs text-gray-400">{init.effortHours}h</span>
                  <span
                    className={`text-xs ${
                      init.complexity === 'low'
                        ? 'text-green-500'
                        : init.complexity === 'medium'
                          ? 'text-yellow-500'
                          : 'text-red-500'
                    }`}
                  >
                    {init.complexity}
                  </span>
                </div>
              </div>
            </label>
          ))}
        </div>
      </div>

      <div className="flex gap-3">
        <button
          onClick={onBack}
          className="flex-1 py-3 border border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-colors"
        >
          Back
        </button>
        <button
          onClick={onBuildPlan}
          disabled={selectedIds.length === 0}
          className="flex-1 py-3 bg-legacy-600 text-white font-medium rounded-xl hover:bg-legacy-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          Build Plan ({selectedIds.length} initiatives)
        </button>
      </div>
    </div>
  );
}

function PlanStep({
  plan,
  pricing,
  clientOutput,
  onStartOver,
}: {
  plan: Plan;
  pricing: PricingResult;
  clientOutput: ClientOutput;
  onStartOver: () => void;
}) {
  return (
    <div className="space-y-6">
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">{clientOutput.title}</h2>
        <p className="text-sm text-gray-600">{clientOutput.overallSummary}</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <MetricCard label="Total Investment" value={`${pricing.currency} ${pricing.total.toLocaleString()}`} />
        <MetricCard label="Initiatives" value={plan.initiatives.length.toString()} />
        <MetricCard label="Phases" value={plan.phases.length.toString()} />
        <MetricCard label="Timeline" value={clientOutput.estimatedTimeline} />
      </div>

      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Phase Breakdown</h2>
        <div className="space-y-4">
          {clientOutput.phases.map((phase) => (
            <div key={phase.phase} className="border border-gray-100 rounded-lg p-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-semibold text-gray-900">Phase {phase.phase}</span>
                <span className="text-xs text-gray-400">{phase.totalEffortHours}h</span>
              </div>
              <ul className="space-y-1">
                {phase.initiativeNames.map((name) => (
                  <li key={name} className="text-sm text-gray-600 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-legacy-400 flex-shrink-0" />
                    {name}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {pricing.appliedBundles.length > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <p className="text-sm font-medium text-green-800 mb-1">Bundle Savings Applied</p>
          {pricing.appliedBundles.map((b) => (
            <p key={b.bundleId} className="text-sm text-green-700">
              {b.bundleName}: {b.discountPercent}% off ({pricing.currency} {b.discountAmount.toLocaleString()} saved)
            </p>
          ))}
        </div>
      )}

      <button
        onClick={onStartOver}
        className="w-full py-3 border border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-colors"
      >
        Start New Assessment
      </button>
    </div>
  );
}

function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="card text-center">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-lg font-bold text-gray-900 mt-1">{value}</p>
    </div>
  );
}
